import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, IObjectData, IRuleset, ISystem, ISystemRole, ITableColumn} from '../../../../index';
import {global} from "../../../../appContext";
import {KOSubscriptionTracker} from "../../../../util/KOSubscriptionTracker";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../../../constants";
import PromiseRequest from "../../../../req/PromiseRequest";
import {IIsRolePropertyMutable, ISystemMutable, ISystemRoleMutable, TErrorManagers} from "../../index";
import {storeObservable} from "../../../../util/sessionstore";
import pubsub from "../../../../pubsub";
import {nsFa} from "../../../fa/fn";
import {REQUEST_MESSAGE} from "../../../../constants/Language";
import {getSearchObjectParams} from "../../../../util/request";
import {nsRuleset} from "../../../ruleset/nsRuleset";
import {
    createErrorMessage,
    getObjectType,
    getRawSyncFieldValue,
    isNullOrUndefinedOrEmptyString,
    validateEmail
} from "../../../../util/misc";
import {nsSyncFields} from "../../../../req/nsSyncFields";
import {ErrorObject} from "../../../../error/ErrorObject";
import {DropdownMenu} from "../../../dropdown/menu";
import {nsRulesetVariableCrud} from "../../../ruleset/variable/nsRulesetVariableCrud";
import {MULTI_COLLAPSE_DEFAULT} from "../../../meta/multi";
import i = nsFa.i;
import mutableHtml from './mutable.html?raw';

export let template = mutableHtml;

const MEMBEROFGROUPS_VISIBLE_DEFAULT = MULTI_COLLAPSE_DEFAULT;

interface IParams {
    md: ISystemMutable
    errorManagers: TErrorManagers
}

/** Roles (and their ErrorObjects) outlive the tab component; validators must only be attached once. */
const rolesWithValidatorsAttached = new WeakSet<ISystemRoleMutable>();

export class viewModel<T extends ISystem> {
    private readonly asc: KnockoutObservable<boolean> = ko.observable(true);
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly _pagenr: KnockoutObservable<number | null> = ko.observable(null);
    private readonly pagenr: KnockoutObservable<number | null> = ko.observable(null);
    private readonly pages: KnockoutComputed<number> = ko.pureComputed(() => Math.ceil(ko.unwrap(this.roles()).length / this.resultsprpage()));
    private readonly resultsprpage = storeObservable('num.search.results');
    private readonly roles: KnockoutObservableArray<ISystemRoleMutable>
    private readonly sortcol: KnockoutObservable<string> = ko.observable('roleenum');
    private readonly totalresults: KnockoutComputed<number> = ko.pureComputed(() => ko.unwrap(this.roles()).length);

    protected isPropertyMutable: IIsRolePropertyMutable = {
        availableto: true,
        availableto_id: true,
        availabletorulesets: true,
        description: true,
        id: true,
        memberofgroup: true,
        memberofgroups: true,
        memberruleset: true,
        metadata: true,
        name: true,
        overrides: true,
        priority: true,
        purgemanualdepartmentpermissions: true,
        recursive: true,
        roleenum: true,
        rolememberruleset: true,
        rolememberrulesets: true,
        systemid: true,
        systemowner: true,
    }

    private readonly Table: ITableColumn[] = [
        {
            key: 'id',
        },
        {
            key: 'name',
        },
        {
            key: 'roleenum',
            label: 'external id',
        },
        {
            key: 'priority',
            validator: () => this.showPriority(),
        },
        {
            key: 'memberofgroup',
            label: 'join group',
        },
        {
            key: 'rolememberruleset',
            label: 'receives automatic permission',
        },
        {
            key: 'availabletorulesets',
            label: 'can be given permission',
        },
        {
            key: 'recursive',
            label: 'department specific permission',
            center: true,
        },
        {
            key: 'purgemanualdepartmentpermissions',
            label: 'remove manually assigned department permissions',
            center: true,
            validator: () => this.md.purgemanualdepartmentpermissions(),
        },
        {
            key: 'system owner',
            label: 'system owner',
        },
        {
            key: 'func',
            label: '',
            validator: () => this.isAnyNew(),
        },
    ]
    private readonly md: ISystemMutable;
    private readonly errorManagers: TErrorManagers;
    private readonly ObjectTypeId = ObjectTypeId;
    private readonly isWaiting: KnockoutObservable<boolean> = ko.observable(false)
    private readonly req_searchobject__groups: PromiseRequest = global.getPromiseRequest('searchobject');
    private groups!: IObjectData[];
    private rulesets!: IRuleset[];
    private readonly memberofgroupsVisibleCount = new WeakMap<ISystemRoleMutable, KnockoutObservable<number>>();

    constructor(params: IParams) {
        this.md = params.md;

        if (this.md.isSystemMegaEadm) {
            let key: keyof ISystemRole
            for (key in this.isPropertyMutable) {
                this.isPropertyMutable[key] = false
            }
            this.isPropertyMutable.memberofgroup = true
            this.isPropertyMutable.memberofgroups = true
            this.isPropertyMutable.rolememberruleset = true
            this.isPropertyMutable.rolememberrulesets = true
        }

        this.errorManagers = params.errorManagers;
        this.roles = this.md.roles;
        this.roles().forEach(r => this.trackRoleErrObj(r))
        pubsub.on(this, 'go.to.last.page', () => this.changePageNrAndNotify(Math.floor(this.totalresults() / this.resultsprpage())))
        this.kosub.subscribe(this.resultsprpage, () => this.changePageNrAndNotify(0))
        this.kosub.subscribe(this.rolesPage, () => {
            requestAnimationFrame(() => {
                if ((this._pagenr() ?? 0) > 0 && this.rolesPage().length == 0) {
                    this._pagenr((this._pagenr() ?? 0) - 1);
                }
            })
        })
        this.preload()
    }

    private preload() {
        this.isReady(false)
        this.messageData({message: REQUEST_MESSAGE})
        const groupIds = _.flatten(this.roles().map((o: ISystemRoleMutable) => o.memberofgroups()).map((o) => o.map(o => o())))
        this.req_searchobject__groups.abort()
        Promise.resolve()
            .then(() => nsSyncFields.getSyncfieldIdsForObjectTypeId(ObjectTypeId.Group))
            .then((syncfieldids: string) => {
                return this.req_searchobject__groups
                    .request(getSearchObjectParams({
                        objecttypeid: ObjectTypeId.Group,
                        resultsprpage: MAX_RESULTS_PER_PAGE,
                        jruleset: JSON.stringify(nsRuleset.generateRuleset({
                            objecttype: getObjectType(ObjectTypeId.Group),
                            rules: [nsRuleset.generateRule({
                                argument: groupIds.join(','),
                                argumentoperator: 'Er lik en av',
                                attribute: 'Id',
                            })],
                        })),
                        syncfieldids: syncfieldids,
                    }))
                    .then((data: IObjectData[]) => {
                        this.groups = data
                    })
            })
            .then(() => {
                return nsRuleset.getAll()
                    .then((data: IRuleset[]) => {
                        this.rulesets = data
                        this.roles().forEach(r => {
                            this.sortRulesetArray(r.rolememberrulesets)
                            this.sortRulesetArray(r.availabletorulesets)
                        })
                    })
            })
            .then(() => {
                this.messageData(null)
                this.pagenr(this._pagenr())
                this.isReady(true)
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    private changePageNrAndNotify(newPageNr: number) {
        const pageBefore = this._pagenr();
        this._pagenr(newPageNr);
        const pageAfter = this._pagenr();
        if (pageBefore != pageAfter) {
            global.snackbarData({message: `${i('fa-info-circle')} ((changed to page number|number:${(pageAfter ?? 0) + 1})).`});
        }
    }

    private onPickGroupClick(r: ISystemRoleMutable) {
        // Same intent as Staging excludeObjectIds: already-joined groups stay visible but not selectable.
        const excludedGroupIds = r.memberofgroups().map(id => id());
        global.dialogManager.addComponent({
            title: `${i('fa-plus')} ((add subject|s:((group|ac|p))))`,
            fullWidth: true,
            content: {
                name: 'component/systemaccess/roles/instance/pick-groups',
                params: {
                    excludedGroupIds,
                    onAdd: (groups: IObjectData[]) => {
                        groups.forEach(group => {
                            if (!this.groups.find(o => o.id === group.id)) {
                                this.groups.push(group);
                            }
                            if (!r.memberofgroups().some(id => id() === group.id)) {
                                r.memberofgroups.push(ko.observable(group.id));
                            }
                        });
                        this.syncMemberofgroup(r);
                        const visibleCount = this.getMemberofgroupsVisibleCount(r);
                        const len = r.memberofgroups().length;
                        if (visibleCount() < len) {
                            visibleCount(len);
                        }
                    },
                },
            },
        });
    }

    onRemoveRoleClick(obj: ISystemRoleMutable) {
        this.unregisterRoleErrObjs(obj)
        const index = this.md.roles().indexOf(obj);
        this.md.roles.remove(obj);
        global.snackbarData({
            message: `subject is removed|s:((permission))`,
            undo: () => {
                this.registerRoleErrObjs(obj)
                this.md.roles.splice(index, 0, obj)
            },
        });
    }

    private rolesPage = ko.pureComputed((): ISystemRoleMutable[] => {
        const start = (this._pagenr() ?? 0) * (this.resultsprpage() ?? 0);
        const end = start + (this.resultsprpage() ?? 0);
        return this.roles().slice(start, end);
    })

    private getGroup(id: number): IObjectData | undefined {
        return this.groups.find(o => o.id == id)
    }

    private getRuleset(id: number): IRuleset | undefined {
        return this.rulesets.find(o => o.id == id)
    }

    private onAddRoleClick() {
        pubsub.trigger('go.to.last.page');
        const r: ISystemRoleMutable = {
            $original: {
                availableto: '',
                availableto_id: 0,
                availabletorulesets: '',
                description: '',
                id: 0,
                memberofgroup: 0,
                memberofgroups: '',
                memberruleset: null,
                metadata: '',
                name: '',
                overrides: [],
                priority: 0,
                purgemanualdepartmentpermissions: false,
                recursive: false,
                roleenum: 0,
                rolememberruleset: 0,
                rolememberrulesets: '',
                systemid: 0,
                systemowner: '',
            },
            availableto_id: ko.observable(0),
            availabletorulesets: ko.observableArray<KnockoutObservable<number>>([]),
            description: ko.observable(''),
            id: 0,
            memberofgroup: ko.observable(0),
            memberofgroups: ko.observableArray<KnockoutObservable<number>>([]),
            metadata: ko.observable(''),
            name: ko.observable(''),
            priority: ko.observable(0),
            purgemanualdepartmentpermissions: ko.observable(false),
            recursive: ko.observable(false),
            roleenum: ko.observable(0),
            rolememberruleset: ko.observable(0),
            rolememberrulesets: ko.observableArray<KnockoutObservable<number>>([]),
            systemowner: ko.observable(''),
            errName: new ErrorObject({name: 'name'}),
            errRoleEnum: new ErrorObject({name: 'roleEnum'}),
            errSystemOwner: new ErrorObject({name: 'systemOwner'}),
            errPriority: new ErrorObject({name: 'priority'}),
        };
        this.trackRoleErrObj(r);
        this.md.roles.push(r);
    }

    private trackRoleErrObj(obj: ISystemRoleMutable) {
        if (!rolesWithValidatorsAttached.has(obj)) {
            rolesWithValidatorsAttached.add(obj);
            // Capture md — validators outlive this tab component across remounts.
            const md = this.md;
            obj.errName.addValidator({
                fn: () => !!obj.name(),
                message: 'invalid name',
            })
            obj.errRoleEnum.addValidator({
                fn: () => !md.roles().find(r => r !== obj && r.roleenum() == obj.roleenum()),
                message: 'must be unique',
                prerequisite: () => !!obj.roleenum() && obj.roleenum() != 0,
            })
            obj.errSystemOwner.addValidator({
                fn: () => {
                    const emails = obj.systemowner().split(',').map(email => email.trim());
                    return emails.every(email => validateEmail(email));
                },
                message: 'invalid email',
                prerequisite: () => !isNullOrUndefinedOrEmptyString(obj.systemowner()),
            })
            obj.errPriority.addValidator(
                {
                    fn: () => obj.priority() > 0 && !isNaN(obj.priority()) && obj.priority() % 1 == 0,
                    message: 'Invalid',
                    prerequisite: () => !!md.prioritybasedroles(),
                },
                {
                    fn: () => !md.roles().find(r => {
                        const a = r !== obj;
                        const b = r.priority() == obj.priority();
                        return a && b
                    }),
                    message: 'Must be unique',
                    prerequisite: () => !!md.prioritybasedroles(),
                },
            )
        }
        this.registerRoleErrObjs(obj);
    }

    private registerRoleErrObjs(obj: ISystemRoleMutable) {
        this.errorManagers.systemaccessroles.addErrorObject(obj.errName);
        this.errorManagers.systemaccessroles.addErrorObject(obj.errRoleEnum);
        this.errorManagers.systemaccessroles.addErrorObject(obj.errSystemOwner);
        this.errorManagers.systemaccessroles.addErrorObject(obj.errPriority);
    }

    private unregisterRoleErrObjs(obj: ISystemRoleMutable) {
        this.errorManagers.systemaccessroles.removeErrorObject(obj.errName);
        this.errorManagers.systemaccessroles.removeErrorObject(obj.errRoleEnum);
        this.errorManagers.systemaccessroles.removeErrorObject(obj.errSystemOwner);
        this.errorManagers.systemaccessroles.removeErrorObject(obj.errPriority);
    }

    private showPriority = ko.pureComputed((): boolean => {
        return (!this.md.multiplerolesamedepartment() && !this.md.multiplerolesamecompany()) && this.md.prioritybasedroles()
    })

    private showPurge = ko.pureComputed((): boolean => {
        return this.md.purgemanualdepartmentpermissions()
    })

    private getRulesetDefaultMenu(arr: KnockoutObservableArray<KnockoutObservable<number>>): DropdownMenu {
        const ret = new DropdownMenu({
            items: [{
                onClick: () => {
                    const selectedRulesetIds = arr().map(idObs => idObs());
                    nsRulesetVariableCrud.showDialog_add((o) => {
                        const ruleset = o as IRuleset;
                        if (arr().some(idObs => idObs() === ruleset.id)) {
                            return;
                        }
                        arr.push(ko.observable(ruleset.id));
                        this.sortRulesetArray(arr);
                    }, selectedRulesetIds);
                },
                description: 'add subject|s:((ruleset|ac))',
                icon: 'fa-hand-point-up',
                label: 'add',
            }, {
                onClick: () => nsRulesetVariableCrud.showDialog_create((o) => {
                    const ruleset = o as IRuleset;
                    const id = ko.observable(ruleset.id);
                    arr.push(id)
                    this.sortRulesetArray(arr)
                }),
                description: 'create s|s:((ruleset|ac))',
                icon: 'fa-plus',
                label: 'create',
            }]
        })
        ret.button_tt = 'ruleset'
        return ret
    }

    private sortRulesetArray(arr: KnockoutObservableArray<KnockoutObservable<number>>): void {
        arr.sort((a, b) => {
            const nameA = this.rulesets.find(o => o.id == a())?.name ?? ''
            const nameB = this.rulesets.find(o => o.id == b())?.name ?? ''
            return nameA.localeCompare(nameB)
        })
    }

    private isAnyNew(): boolean {
        return !!this.md.roles().find(o => !o.id)
    }

    private getNumCols(): number {
        return this.Table.filter(o => {
            switch (o.key) {
                case 'id':
                case 'func':
                    return false
                default:
                    return o.validator?.() ?? true
            }
        }).length
    }

    private onRemoveMemberofgroupClick(role: ISystemRoleMutable, idObs: KnockoutObservable<number>): void {
        role.memberofgroups.remove(idObs);
        this.syncMemberofgroup(role);
        const visibleCount = this.getMemberofgroupsVisibleCount(role);
        const len = role.memberofgroups().length;
        if (len > MEMBEROFGROUPS_VISIBLE_DEFAULT) {
            visibleCount(Math.min(visibleCount(), len));
        } else {
            visibleCount(MEMBEROFGROUPS_VISIBLE_DEFAULT);
        }
    }

    /** Keep legacy singular memberofgroup aligned with the multi-group list (BE validates it). */
    private syncMemberofgroup(role: ISystemRoleMutable): void {
        role.memberofgroup(role.memberofgroups()[0]?.() ?? 0);
    }

    private getMemberofgroupsSorted(ids: ISystemRoleMutable['memberofgroups']): KnockoutObservable<number>[] {
        return _.sortBy(ids(), (id: KnockoutObservable<number>) => {
            const group = this.getGroup(id());
            if (!group) {
                return true
            }
            return getRawSyncFieldValue('Name', group.syncfields)?.toLowerCase()
        })
    }

    private getMemberofgroupsVisibleCount(role: ISystemRoleMutable): KnockoutObservable<number> {
        let count = this.memberofgroupsVisibleCount.get(role);
        if (!count) {
            count = ko.observable(MEMBEROFGROUPS_VISIBLE_DEFAULT);
            this.memberofgroupsVisibleCount.set(role, count);
        }
        return count;
    }

    private getMemberofgroupsVisible(role: ISystemRoleMutable): KnockoutObservable<number>[] {
        const sorted = this.getMemberofgroupsSorted(role.memberofgroups);
        return sorted.slice(0, this.getMemberofgroupsVisibleCount(role)());
    }

    dispose() {
        this.roles().forEach(r => this.unregisterRoleErrObjs(r));
        this.kosub.unsuscribeAll();
        this.req_searchobject__groups.abort()
        pubsub.off(this);
    }
}
